import React, { FC, PropsWithChildren } from 'react';
import { ThemeProvider, createTheme } from '@mui/material';

declare module '@mui/material/styles' {
  interface Theme {
    colors: {
      primary: string;
      secondary: string;
      error: string;
      warning: string;
      success: string;
      navBackgroundColor: string;
    };
  }
  // allow configuration using `createTheme`
  interface ThemeOptions {
    colors: {
      primary: string;
      secondary?: string;
      error?: string;
      warning?: string;
      success?: string;
      navBackgroundColor: string
    };
  }
}

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    // removes the `xs, sm, md, lg, and xl` breakpoint
    xs: false;
    sm: false;
    md: false;
    lg: false;
    xl: false;
    // add custom breakpoints here to be used across the app
    mobileS: true;
    mobile: true;
    tabletS: true;
    tablet: true;
    tabletL: true;
    laptop: true;
    laptopM: true;
    laptopL: true;
    laptopXL: true;
    desktop: true;
    desktopL: true;
  }
  interface TypographyVariants {
    nav: React.CSSProperties;
    primaryFont: React.CSSProperties;
    secondaryFont: React.CSSProperties;
    errorFont: React.CSSProperties;
  }
  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    nav?: React.CSSProperties;
    primaryFont?: React.CSSProperties;
    secondaryFont?: React.CSSProperties;
    errorFont?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    nav: true;
    primaryFont: true;
    secondaryFont: true;
    errorFont: true;
  }
}

const MuiThemeProvider: FC<PropsWithChildren> = ({ children }) => {
  const theme = createTheme({
    // Custom non-palette variables
    colors: {
      primary: '#123885',
      navBackgroundColor: '#282e46'
    },
    typography: {
      fontFamily: [
        "Atyp BL",
        "Helvetica",
        "Arial",
        "sans-serif"
      ].join(','),
      fontSize: 16,
      nav: {
        fontFamily: [
          "Atyp BL",
          "Helvetica",
          "Arial",
          "sans-serif"
        ].join(','),
        fontSize: 16
      },
      primaryFont: {
        fontFamily: [
          "Atyp BL",
          "Helvetica",
          "Arial",
          "sans-serif"
        ].join(',')
      },
      secondaryFont: {
        fontFamily: [
          "Seasons",
          "Helvetica",
          "Arial",
          "sans-serif"
        ].join(','),
      },
      errorFont: {
        fontFamily: [
          "Atyp BL",
          "Helvetica",
          "Arial",
          "sans-serif"
        ].join(','),
        color: "#cb2a20",
        textTransform: "uppercase",
        fontWeight: "400"
      }
    },
    breakpoints: {
      values: {
        mobileS: 0,
        mobile: 425,
        tabletS: 468,
        tablet: 768,
        tabletL: 930,
        laptop: 1024,
        laptopM: 1250,
        laptopL: 1440,
        laptopXL: 1700,
        desktop: 1920,
        desktopL: 2560
      },
    },
  })

  return (
    <ThemeProvider theme={theme}>
      {children}
    </ThemeProvider>
  )
};

export default MuiThemeProvider;
